import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { MemberTrainingService } from "services/memberTraining.service";
import { MemberService } from "services/member.service";
import { TrainingService } from "services/training.service";
import {
  ISaveMemberTrainingDTO,
  IUpdateMemberTrainingDTO,
  IMemberResponseDTO,
  ITrainingResponseDTO,
} from "models/church.types";

interface FormErrors {
  memberId?: string;
  trainingId?: string;
  grade?: string;
}

export default function MemberTrainingRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [trainings, setTrainings] = useState<
    { value: number; label: string }[]
  >([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const [memberTraining, setMemberTraining] = useState<
    ISaveMemberTrainingDTO | IUpdateMemberTrainingDTO
  >({
    memberId: 0,
    trainingId: 0,
    grade: "",
    completed: false,
    completionDate: "",
    certificateIssued: false,
  });

  // CONFIGURATION DU STYLE REACT-SELECT (Support du Portal global et charte graphique)
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused
        ? "#D4AF37"
        : errors.memberId || errors.trainingId
          ? "#ef4444"
          : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Chargement simultané des référentiels Membres et Formations
        const [membersRes, trainingsRes] = await Promise.all([
          MemberService.getAll(0, 1000),
          TrainingService.getAll(0, 1000),
        ]);

        setMembers(
          membersRes.data.map((m: IMemberResponseDTO) => ({
            value: m.id,
            label:
              `${m.lastName || ""} ${m.firstName || ""}`.trim() ||
              `Membre #${m.id}`,
          })),
        );

        setTrainings(
          trainingsRes.data.map((t: ITrainingResponseDTO) => ({
            value: t.id,
            label: t.title || `Formation #${t.id}`,
          })),
        );

        // Chargement de l'entité existante si modification
        if (isEditMode && id) {
          const data = await MemberTrainingService.getById(parseInt(id));
          setMemberTraining({
            id: data.id,
            memberId: data.memberId,
            trainingId: data.trainingId,
            grade: data.grade || "",
            completed: data.completed ?? false,
            completionDate: data.completionDate
              ? data.completionDate.split("T")[0]
              : "",
            certificateIssued: data.certificateIssued ?? false,
          });
        }
      } catch (error) {
        console.error(
          "Erreur de chargement des ressources académiques :",
          error,
        );
        toast.error(
          "Erreur lors du chargement des dictionnaires (membres/formations)",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation locale
    const newErrors: FormErrors = {};
    if (!memberTraining.memberId || memberTraining.memberId === 0) {
      newErrors.memberId = "Veuillez sélectionner un membre";
    }
    if (!memberTraining.trainingId || memberTraining.trainingId === 0) {
      newErrors.trainingId = "Veuillez sélectionner une formation";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      // RESOLUTION DU SOUCI DE DESERIALIZATION :
      // On convertit la date "YYYY-MM-DD" du input au format complet attendu par l'Instant Java
      let formattedDate = null;
      if (memberTraining.completed && memberTraining.completionDate) {
        // Ajout manuel des composants temporels de minuit au standard UTC (Z)
        formattedDate = `${memberTraining.completionDate}T00:00:00Z`;
      }

      const payload = {
        ...(isEditMode
          ? { id: (memberTraining as IUpdateMemberTrainingDTO).id }
          : {}),
        memberId: Number(memberTraining.memberId),
        trainingId: Number(memberTraining.trainingId),
        grade: memberTraining.grade?.trim() || null,
        completed: memberTraining.completed,
        completionDate: formattedDate, // Envoi de la string ISO valide
        certificateIssued: memberTraining.certificateIssued,
      };

      if (isEditMode) {
        await MemberTrainingService.update(payload as IUpdateMemberTrainingDTO);
        toast.success("Parcours de formation mis à jour");
      } else {
        await MemberTrainingService.create(payload as ISaveMemberTrainingDTO);
        toast.success("Inscription à la formation validée");
      }
      history.push("/admin/member-trainings");
    } catch (error: any) {
      console.error("Erreur d'enregistrement :", error);
      if (error.response?.status === 400 || error.response?.status === 409) {
        toast.error("Conflit : Ce membre est déjà inscrit à cette formation.");
      } else {
        toast.error("Erreur technique lors du traitement de l'enregistrement.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-start md:justify-center mt-14 p-4 w-full min-h-screen py-12 md:py-6">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-graduation-cap mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Modifier l'état académique"
                  : "Inscrire à un cursus"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Sélection de la formation */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-book-open mr-2"></i>Programme de
                    formation
                  </label>
                  <Select
                    options={trainings}
                    styles={customSelectStyles}
                    placeholder="Chercher un cursus..."
                    value={
                      trainings.find(
                        (opt) => opt.value === memberTraining.trainingId,
                      ) || null
                    }
                    onChange={(selected) => {
                      setMemberTraining((prev) => ({
                        ...prev,
                        trainingId: selected?.value || 0,
                      }));
                      if (errors.trainingId)
                        setErrors((prev) => ({
                          ...prev,
                          trainingId: undefined,
                        }));
                    }}
                    noOptionsMessage={() => "Aucun cursus trouvé"}
                    menuPortalTarget={document.body}
                  />
                  {errors.trainingId && (
                    <p className="text-red-500 text-[10px] mt-1 italic uppercase tracking-tighter">
                      {errors.trainingId}
                    </p>
                  )}
                </div>

                {/* Sélection du membre */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-user-graduate mr-2"></i>Nom du membre
                    étudiant
                  </label>
                  <Select
                    options={members}
                    styles={customSelectStyles}
                    placeholder="Taper le nom du membre..."
                    value={
                      members.find(
                        (opt) => opt.value === memberTraining.memberId,
                      ) || null
                    }
                    onChange={(selected) => {
                      setMemberTraining((prev) => ({
                        ...prev,
                        memberId: selected?.value || 0,
                      }));
                      if (errors.memberId)
                        setErrors((prev) => ({ ...prev, memberId: undefined }));
                    }}
                    noOptionsMessage={() => "Aucun membre trouvé"}
                    menuPortalTarget={document.body}
                  />
                  {errors.memberId && (
                    <p className="text-red-500 text-[10px] mt-1 italic uppercase tracking-tighter">
                      {errors.memberId}
                    </p>
                  )}
                </div>

                {/* Interrupteurs : État d'avancement & Certificat */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Statut Cursus */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-toggle-on mr-2"></i>État d'avancement
                    </label>
                    <div className="flex items-center gap-4 bg-[#0F172A] p-3 rounded-lg border border-slate-700">
                      <button
                        type="button"
                        onClick={() =>
                          setMemberTraining((prev) => ({
                            ...prev,
                            completed: false,
                          }))
                        }
                        className={`flex-1 py-2 rounded-lg font-black text-[11px] uppercase tracking-wider transition-all border ${
                          !memberTraining.completed
                            ? "bg-amber-500/10 text-amber-400 border-amber-500/40 shadow-inner"
                            : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                        }`}
                      >
                        En Cours
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setMemberTraining((prev) => ({
                            ...prev,
                            completed: true,
                          }))
                        }
                        className={`flex-1 py-2 rounded-lg font-black text-[11px] uppercase tracking-wider transition-all border ${
                          memberTraining.completed
                            ? "bg-green-500/10 text-green-400 border-green-500/40 shadow-inner"
                            : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                        }`}
                      >
                        Terminée
                      </button>
                    </div>
                  </div>

                  {/* Certificat délivré */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-certificate mr-2"></i>Délivrance du
                      Diplôme
                    </label>
                    <div className="flex items-center gap-4 bg-[#0F172A] p-3 rounded-lg border border-slate-700">
                      <button
                        type="button"
                        onClick={() =>
                          setMemberTraining((prev) => ({
                            ...prev,
                            certificateIssued: false,
                          }))
                        }
                        className={`flex-1 py-2 rounded-lg font-black text-[11px] uppercase tracking-wider transition-all border ${
                          !memberTraining.certificateIssued
                            ? "bg-slate-800 text-slate-400 border-slate-700"
                            : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                        }`}
                      >
                        Non délivré
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setMemberTraining((prev) => ({
                            ...prev,
                            certificateIssued: true,
                          }))
                        }
                        className={`flex-1 py-2 rounded-lg font-black text-[11px] uppercase tracking-wider transition-all border ${
                          memberTraining.certificateIssued
                            ? "bg-blue-500/10 text-blue-400 border-blue-500/40 shadow-inner"
                            : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                        }`}
                      >
                        <i className="fas fa-award mr-1.5"></i>Émis / Signé
                      </button>
                    </div>
                  </div>
                </div>
                {/* Bloc Note & Date (Grid 2 colonnes) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Note / Évaluation */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-star mr-2"></i>Note finale /
                      Évaluation
                    </label>
                    <input
                      type="text"
                      placeholder={
                        memberTraining.completed
                          ? "Ex: A+, 16/20, Validé..."
                          : "Formation en cours..."
                      }
                      disabled={
                        !memberTraining.completed
                      } /* CORRECTION : Désactivé si non complété */
                      value={
                        memberTraining.completed
                          ? memberTraining.grade || ""
                          : ""
                      } /* Optionnel : vide le champ visuellement si en cours */
                      onChange={(e) =>
                        setMemberTraining((prev) => ({
                          ...prev,
                          grade: e.target.value,
                        }))
                      }
                      /* CORRECTION : Ajout des classes disabled:opacity-30 et disabled:cursor-not-allowed */
                      className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Date de complétion */}
                  <div>
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                      <i className="fas fa-calendar-check mr-2"></i>Date de fin
                      / validation
                    </label>
                    <input
                      type="date"
                      disabled={!memberTraining.completed}
                      value={
                        memberTraining.completed
                          ? memberTraining.completionDate || ""
                          : ""
                      }
                      onChange={(e) =>
                        setMemberTraining((prev) => ({
                          ...prev,
                          completionDate: e.target.value,
                        }))
                      }
                      className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#D4AF37] outline-none transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* Barre d'Actions */}
              <div className="mt-10 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
                <button
                  type="button"
                  onClick={() => history.push("/admin/member-trainings")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg hover:shadow-[#D4AF37]/20 disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Inscrire l'étudiant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
